import { WorldPoint, BuildingBox } from '../domain/types.ts';
import { hasObstacleBetween } from './visibility.ts';

export interface VisionObserver {
  x: number;
  y: number;
  facingAngle: number;
  fov: number; // Góc mở tầm nhìn tính bằng radians (vd: Math.PI / 2.5 ~ 72 độ)
  range: number; // Tầm nhìn xa tối đa (mét)
}

/**
 * Chuẩn hóa góc về khoảng [-PI, PI]
 */
export function normalizeAngle(angle: number): number {
  let a = angle % (Math.PI * 2);
  if (a > Math.PI) a -= Math.PI * 2;
  if (a < -Math.PI) a += Math.PI * 2;
  return a;
}

/**
 * Kiểm tra xem quan sát viên (NPC/Camera) có nhìn thấy mục tiêu hay không:
 * 1. Bán kính khoảng cách <= range
 * 2. Nằm trong hình quạt thị giác (Cone of Vision) góc fov
 * 3. Không bị các khối nhà (BuildingBox) che chắn tầm nhìn (Line of Sight)
 */
export function canSeeTarget(
  observer: VisionObserver,
  target: WorldPoint,
  buildings: BuildingBox[] = []
): boolean {
  const dx = target.x - observer.x;
  const dy = target.y - observer.y;
  const dist = Math.hypot(dx, dy);

  // 1. Kiểm tra cự ly
  if (dist > observer.range || dist < 0.05) {
    return false;
  }

  // 2. Kiểm tra góc nhìn (FOV)
  if (observer.fov < Math.PI * 1.95) {
    const angleToTarget = Math.atan2(dy, dx);
    const angleDiff = Math.abs(normalizeAngle(angleToTarget - observer.facingAngle));
    if (angleDiff > observer.fov / 2) {
      return false;
    }
  }

  // 3. Kiểm tra vật cản tòa nhà (Raycast Line of Sight)
  if (buildings.length > 0) {
    const isBlocked = hasObstacleBetween(
      { x: observer.x, y: observer.y },
      target,
      buildings
    );
    if (isBlocked) {
      return false;
    }
  }

  return true;
}
